'use client';

import type { TFunction } from 'i18next';

import type { BulkEditDataColumn } from '../bulk-edit-columns';
import type { BulkEditRow, BulkEditRowEdits } from '../bulk-edit.types';
import type { BulkEditStoreCurrencies } from '../bulk-edit-currencies';
import { isCellDirty, isRowPriceExternallyManaged, isVariantFieldDisabled } from '../bulk-edit.utils';
import { BulkEditPriceCell, isPriceCellDirty } from './bulk-edit-price-cell';
import {
  BulkEditBadgePicker,
  BulkEditBadgeSelect,
  buildActiveStatusOptions,
  buildDefaultVariantOptions,
  buildIntervalOptions,
  buildRecurringOptions,
  buildStockStatusOptions,
  buildYesNoOptions,
} from './bulk-edit-badge-select';
import {
  BulkEditEmptyDash,
  BulkEditFieldShell,
  BulkEditTextInput,
} from './bulk-edit-field-cell';
import { BulkEditTaxCategoryCell } from './bulk-edit-tax-category-cell';

interface BulkEditRowFieldsProps {
  row: BulkEditRow;
  column: BulkEditDataColumn;
  edits: Map<string, BulkEditRowEdits>;
  storeCurrencies: BulkEditStoreCurrencies;
  t: TFunction;
  isCreditEnabled?: boolean;
  onUpdate: (patch: Record<string, unknown>) => void;
}

export function BulkEditRowFields({
  row,
  column,
  edits,
  storeCurrencies,
  t,
  isCreditEnabled = false,
  onUpdate,
}: BulkEditRowFieldsProps) {
  const isProduct = row.kind === 'product';
  const variantDisabled = isVariantFieldDisabled(isProduct, row.hasChildVariants);
  const priceLocked = isRowPriceExternallyManaged(row);
  const isDirty = (field: string) => isCellDirty(row.key, field, edits);

  switch (column.id) {
    case 'description':
      return (
        <BulkEditFieldShell
          width={column.width}
          dirty={isDirty(isProduct ? 'productDescription' : 'variantDescription')}
        >
          {isProduct ? (
            <BulkEditTextInput
              value={row.productDescription}
              onChange={(v) => onUpdate({ productDescription: v })}
            />
          ) : (
            <BulkEditTextInput
              value={row.variantDescription}
              onChange={(v) => onUpdate({ variantDescription: v })}
            />
          )}
        </BulkEditFieldShell>
      );

    case 'unitLabel':
      return (
        <BulkEditFieldShell
          width={column.width}
          dirty={isDirty('unitLabel')}
          disabled={!isProduct}
        >
          {isProduct ? (
            <BulkEditTextInput
              value={row.unitLabel}
              onChange={(v) => onUpdate({ unitLabel: v || null })}
            />
          ) : (
            <BulkEditEmptyDash />
          )}
        </BulkEditFieldShell>
      );

    case 'accountingCode':
      return (
        <BulkEditFieldShell
          width={column.width}
          dirty={isDirty('accountingCode')}
          disabled={!isProduct}
        >
          {isProduct ? (
            <BulkEditTextInput
              mono
              value={row.accountingCode}
              onChange={(v) => onUpdate({ accountingCode: v || null })}
            />
          ) : (
            <BulkEditEmptyDash />
          )}
        </BulkEditFieldShell>
      );

    case 'mappedTaxCategory':
      return (
        <BulkEditFieldShell
          width={column.width}
          dirty={isDirty('mappedTaxCategoryId')}
          disabled={!isProduct}
        >
          {isProduct ? (
            <BulkEditTaxCategoryCell
              row={row}
              onChange={(v) => onUpdate({ mappedTaxCategoryId: v })}
            />
          ) : (
            <BulkEditEmptyDash />
          )}
        </BulkEditFieldShell>
      );

    case 'price':
      return (
        <BulkEditFieldShell
          width={column.width}
          align={column.align}
          dirty={isPriceCellDirty(row.key, edits)}
          disabled={variantDisabled || priceLocked}
        >
          <BulkEditPriceCell
            row={row}
            edits={edits}
            storeCurrencies={storeCurrencies}
            variantDisabled={variantDisabled}
            priceLocked={priceLocked}
            onUpdate={onUpdate}
          />
        </BulkEditFieldShell>
      );

    case 'creditPrice':
      if (!isCreditEnabled) {
        return (
          <BulkEditFieldShell width={column.width} align={column.align} disabled>
            <BulkEditEmptyDash align="right" />
          </BulkEditFieldShell>
        );
      }
      return (
        <BulkEditFieldShell
          width={column.width}
          align={column.align}
          dirty={isDirty('creditPrice')}
          disabled={variantDisabled}
        >
          {variantDisabled ? (
            <BulkEditEmptyDash align="right" />
          ) : (
            <BulkEditTextInput
              type="number"
              min={0}
              step="1"
              align="right"
              value={row.creditPrice ?? ''}
              onChange={(v) =>
                onUpdate({ creditPrice: v === '' ? null : Number.parseInt(v, 10) || null })
              }
              placeholder="—"
            />
          )}
        </BulkEditFieldShell>
      );

    case 'sku':
      return (
        <BulkEditFieldShell
          width={column.width}
          align={column.align}
          dirty={isDirty('sku')}
          disabled={variantDisabled}
        >
          {variantDisabled ? (
            <BulkEditEmptyDash />
          ) : (
            <BulkEditTextInput mono value={row.sku} onChange={(v) => onUpdate({ sku: v })} />
          )}
        </BulkEditFieldShell>
      );

    case 'weight':
      return (
        <BulkEditFieldShell
          width={column.width}
          align={column.align}
          dirty={isDirty('grams')}
          disabled={variantDisabled}
        >
          {variantDisabled ? (
            <BulkEditEmptyDash align="right" />
          ) : (
            <BulkEditTextInput
              type="number"
              min={0}
              align="right"
              value={row.grams ?? ''}
              onChange={(v) => onUpdate({ grams: v === '' ? null : Number(v) })}
            />
          )}
        </BulkEditFieldShell>
      );

    case 'status':
      return (
        <BulkEditFieldShell
          width={column.width}
          dirty={isDirty('active')}
          disabled={isProduct && row.hasChildVariants}
        >
          {isProduct && row.hasChildVariants ? (
            <BulkEditEmptyDash />
          ) : (
            <BulkEditBadgePicker
              value={row.active ? 'active' : 'inactive'}
              onValueChange={(v) => onUpdate({ active: v === 'active' })}
              options={buildActiveStatusOptions(t)}
            />
          )}
        </BulkEditFieldShell>
      );

    case 'stock':
      return (
        <BulkEditFieldShell
          width={column.width}
          dirty={isDirty('inStock')}
          disabled={variantDisabled}
        >
          {variantDisabled ? (
            <BulkEditEmptyDash />
          ) : (
            <BulkEditBadgePicker
              value={row.inStock ? 'in_stock' : 'out_of_stock'}
              onValueChange={(v) => onUpdate({ inStock: v === 'in_stock' })}
              options={buildStockStatusOptions(t)}
            />
          )}
        </BulkEditFieldShell>
      );

    case 'default':
      return (
        <BulkEditFieldShell
          width={column.width}
          dirty={isDirty('isDefault')}
          disabled={variantDisabled}
        >
          {variantDisabled ? (
            <BulkEditEmptyDash />
          ) : (
            <BulkEditBadgeSelect
              value={row.isDefault ? 'yes' : 'no'}
              onValueChange={(v) => onUpdate({ isDefault: v === 'yes' })}
              options={buildDefaultVariantOptions(t)}
            />
          )}
        </BulkEditFieldShell>
      );

    case 'recurring':
      return (
        <BulkEditFieldShell
          width={column.width}
          dirty={isDirty('recurring')}
          disabled={variantDisabled}
        >
          {variantDisabled ? (
            <BulkEditEmptyDash />
          ) : (
            <BulkEditBadgeSelect
              value={row.recurring ? 'recurring' : 'one_time'}
              onValueChange={(v) => onUpdate({ recurring: v === 'recurring' })}
              options={buildRecurringOptions(t)}
            />
          )}
        </BulkEditFieldShell>
      );

    case 'interval':
      return (
        <BulkEditFieldShell
          width={column.width}
          dirty={isDirty('interval')}
          disabled={variantDisabled || !row.recurring}
        >
          {variantDisabled || !row.recurring ? (
            <BulkEditEmptyDash />
          ) : (
            <BulkEditBadgeSelect
              value={row.interval ?? 'month'}
              onValueChange={(v) => onUpdate({ interval: v })}
              options={buildIntervalOptions(t)}
            />
          )}
        </BulkEditFieldShell>
      );

    case 'intervalCount':
      return (
        <BulkEditFieldShell
          width={column.width}
          align={column.align}
          dirty={isDirty('intervalCount')}
          disabled={variantDisabled || !row.recurring}
        >
          {variantDisabled || !row.recurring ? (
            <BulkEditEmptyDash align="right" />
          ) : (
            <BulkEditTextInput
              type="number"
              min={1}
              align="right"
              value={row.intervalCount ?? 1}
              onChange={(v) => onUpdate({ intervalCount: v === '' ? null : Number(v) })}
            />
          )}
        </BulkEditFieldShell>
      );

    case 'shippable':
      return (
        <BulkEditFieldShell width={column.width} dirty={isDirty('isShippable')} disabled={!isProduct}>
          {isProduct ? (
            <BulkEditBadgeSelect
              value={row.isShippable ? 'yes' : 'no'}
              onValueChange={(v) => onUpdate({ isShippable: v === 'yes' })}
              options={buildYesNoOptions(t)}
            />
          ) : (
            <BulkEditEmptyDash />
          )}
        </BulkEditFieldShell>
      );

    case 'taxable':
      return (
        <BulkEditFieldShell width={column.width} dirty={isDirty('isTaxable')} disabled={!isProduct}>
          {isProduct ? (
            <BulkEditBadgeSelect
              value={row.isTaxable ? 'yes' : 'no'}
              onValueChange={(v) => onUpdate({ isTaxable: v === 'yes' })}
              options={buildYesNoOptions(t)}
            />
          ) : (
            <BulkEditEmptyDash />
          )}
        </BulkEditFieldShell>
      );

    case 'sync':
      return (
        <BulkEditFieldShell width={column.width} dirty={isDirty('autoSync')} disabled={!isProduct}>
          {isProduct ? (
            <BulkEditBadgeSelect
              value={row.autoSync ? 'yes' : 'no'}
              onValueChange={(v) => onUpdate({ autoSync: v === 'yes' })}
              options={buildYesNoOptions(t)}
            />
          ) : (
            <BulkEditEmptyDash />
          )}
        </BulkEditFieldShell>
      );

    default:
      return null;
  }
}
